import type { ActionKind, GameView, LogEntry, LogRow, Role } from "@coup/shared";
import { useLayoutEffect, useRef, type ReactNode } from "react";
import { ICONS } from "../../art";
import { Avatar } from "../../components/Avatar";
import { Icon } from "../../components/Icon";
import { RoleChip } from "../../components/RoleChip";
import type { Names } from "./names";
import styles from "./CourtRecord.module.css";

const VERB: Record<ActionKind, string> = {
  income: "takes Income",
  foreignAid: "takes Foreign Aid",
  tax: "claims Tax",
  exchange: "exchanges",
  assassinate: "assassinates",
  steal: "steals from",
  coup: "launches a Coup on",
};

const plural = (n: number) => `+${n} coin${n === 1 ? "" : "s"}`;

type ActionEntry = Extract<LogEntry, { t: "action" }>;

function resultLine(e: ActionEntry, pending: boolean): string {
  const parts: string[] = [];
  if (e.paid) parts.push(`Paid ${e.paid}`);
  if (e.allowed && e.claim) parts.push("Allowed");
  if (e.outcome?.t === "resolved" && e.outcome.coins !== undefined)
    parts.push(plural(e.outcome.coins));
  if (pending && !e.rows.length) parts.push("Waiting for responses…");
  return parts.join(" · ");
}

interface Sub {
  by: string;
  text: string;
  tone?: "bs" | "loss";
  role?: Role;
}

/** Turns log rows into display lines, merging "concedes" with the loss that follows. */
function subRows(e: ActionEntry): Sub[] {
  const out: Sub[] = [];
  const rows: LogRow[] = e.rows;
  rows.forEach((r, i) => {
    if (r.t === "challenge") out.push({ by: r.by, text: "calls Bullshit!", tone: "bs" });
    else if (r.t === "block") out.push({ by: r.by, text: "blocks with", role: r.role });
    else if (r.t === "reveal") out.push({ by: r.by, text: "reveals", role: r.role });
    else if (r.t === "lose") {
      const prev = rows[i - 1];
      const conceded = prev?.t === "concede" && prev.by === r.by;
      out.push({
        by: r.by,
        text: conceded ? "concedes, loses" : "loses",
        tone: "loss",
        role: r.role,
      });
    } else if (r.t === "concede" && rows[i + 1]?.t !== "lose") {
      out.push({ by: r.by, text: "concedes", tone: "loss" });
    }
  });
  const o = e.outcome;
  if (o?.t === "failed")
    out.push({
      by: e.actor,
      text: `action fails${o.refund ? ` · ${o.refund} coins refunded` : ""}`,
    });
  if (o?.t === "fizzled") out.push({ by: e.actor, text: "action has no effect" });
  if (o?.t === "cancelled") out.push({ by: e.actor, text: "action cancelled" });
  return out;
}

interface Props {
  view: GameView;
  names: Names;
  onCollapse: () => void;
}

export function CourtRecord({ view, names, onCollapse }: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const log = view.log;
  const currentIndex = view.action ? log.findLastIndex((e) => e.t === "action") : -1;

  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [log.length, view]);

  const dot = (id: string, size: number) => (
    <Avatar avatar={names.avatar(id)} size={size} ring={1.5} style={{ display: "inline-flex" }} />
  );

  const render = (e: LogEntry, i: number): ReactNode => {
    switch (e.t) {
      case "round":
        return (
          <div key={i} className={styles.divider}>
            <div />
            Round {e.round}
            <div />
          </div>
        );
      case "eliminated":
        return (
          <div key={i} className={styles.note}>
            {names.name(e.playerId)} is exiled
          </div>
        );
      case "kicked":
        return (
          <div key={i} className={styles.note}>
            {names.name(e.playerId)} left the court
          </div>
        );
      case "win":
        return (
          <div key={i} className={styles.note}>
            {names.name(e.playerId)} wins the match
          </div>
        );
      case "action": {
        const result = resultLine(e, i === currentIndex);
        return (
          <div
            key={i}
            className={styles.entry}
            style={{ background: i === currentIndex ? "rgba(242,194,48,0.08)" : "transparent" }}
          >
            <Avatar avatar={names.avatar(e.actor)} size={28} ring={2} />
            <div className={styles.body}>
              <div className={styles.headline}>
                <b>{names.name(e.actor)}</b>
                <span className={styles.verb}>{VERB[e.kind]}</span>
                {e.claim && <RoleChip role={e.claim} />}
                {e.target && (
                  <>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.target}>
                      {dot(e.target, 18)}
                      <b>{names.name(e.target)}</b>
                    </span>
                  </>
                )}
              </div>
              {result && <div className={styles.result}>{result}</div>}
              {subRows(e).map((s, j) => (
                <div key={j} className={styles.sub}>
                  {dot(s.by, 16)}
                  <b>{names.name(s.by)}</b>
                  <span className={s.tone ? styles[s.tone] : styles.verb}>{s.text}</span>
                  {s.role && <RoleChip role={s.role} size="sm" />}
                </div>
              ))}
            </div>
          </div>
        );
      }
    }
  };

  return (
    <>
      <div className={styles.head}>
        <div className={styles.title}>Game Record</div>
        <button
          type="button"
          className={styles.collapse}
          aria-label="Collapse action log"
          onClick={onCollapse}
        >
          <Icon path={ICONS.collapseRight} size={18} />
        </button>
      </div>
      <div
        ref={scroller}
        className={styles.list}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
        }}
      >
        {log.map(render)}
      </div>
    </>
  );
}
